import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useAnalytics } from './hooks/useAnalytics';
import Header from './components/Header';
import HeroSlider from './components/HeroSlider';
import SearchDiscovery from './components/SearchDiscovery';
import CityCelebrations from './components/CityCelebrations';
import FeaturedSections from './components/FeaturedSections';
import WelcomeSection from './components/WelcomeSection';
import Footer from './components/Footer';
import AdminDashboard from './components/AdminDashboard';
import AdminEventManagement from './components/AdminEventManagement';
import AdminUserManagement from './components/AdminUserManagement';
import CommunityUserManagement from './components/CommunityUserManagement';
import CommunityAdminDashboard from './components/CommunityAdminDashboard';
import CommunityDashboard from './components/CommunityDashboard';
import VendorDashboard from './components/VendorDashboard';
import ProtectedRoute from './components/ProtectedRoute';

import ExploreEvents from './components/ExploreEvents';
import SignupPage from './components/SignupPage';
import LoginPage from './components/LoginPage';
import AboutPage from './components/AboutPage';
import FAQ from './components/FAQ';
import ForgotPassword from './components/ForgotPassword';
import Gallery from './components/Gallery';
import Calendar from './components/Calendar';
import Contact from './components/Contact';
import TermsAndConditions from './components/TermsAndConditions';
import Disclaimer from './components/Disclaimer';
import PrivacyPolicy from './components/PrivacyPolicy';
import EventDetailRoute from './components/EventDetailRoute';
import AdSense from './components/AdSense';

// Main App component that provides the AuthProvider
const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

// AppContent component that uses the auth context
const AppContent: React.FC = () => {
  const { user } = useAuth();
  const analytics = useAnalytics();
  const [currentPage, setCurrentPage] = useState<'home' | 'admin' | 'community-admin' | 'vendor-admin' | 'community' | 'users' | 'community-users' | 'all-events' | 'signup' | 'login' | 'about' | 'faq' | 'forgot-password' | 'gallery' | 'calendar' | 'contact' | 'terms' | 'disclaimer' | 'privacy' | 'event-detail'>('home');

  // Simple routing function
  const handleRouteChange = (path: string) => {
    let pageName = 'home';
    
    if (path === '/admin') {
      setCurrentPage('admin');
      pageName = 'admin';
    } else if (path === '/community-admin') {
      setCurrentPage('community-admin');
      pageName = 'community-admin';
    } else if (path === '/vendor-admin') {
      setCurrentPage('vendor-admin');
      pageName = 'vendor-admin';
    } else if (path === '/community') {
      setCurrentPage('community');
      pageName = 'community';
    } else if (path === '/users') {
      setCurrentPage('users');
      pageName = 'users';
    } else if (path === '/community-users') {
      setCurrentPage('community-users');
      pageName = 'community-users';
    } else if (path === '/all-events') {
      setCurrentPage('all-events');
      pageName = 'all-events';
    } else if (path === '/signup') {
      setCurrentPage('signup');
      pageName = 'signup';
    } else if (path === '/login') {
      setCurrentPage('login');
      pageName = 'login';
    } else if (path === '/about') {
      setCurrentPage('about');
      pageName = 'about';
    } else if (path === '/faq') {
      setCurrentPage('faq');
      pageName = 'faq';
    } else if (path === '/forgot-password') {
      setCurrentPage('forgot-password');
      pageName = 'forgot-password';
    } else if (path === '/gallery') {
      setCurrentPage('gallery');
      pageName = 'gallery';
    } else if (path === '/Durga-Puja-Calendar-2025') {
      setCurrentPage('calendar');
      pageName = 'calendar';
    } else if (path === '/contact') {
      setCurrentPage('contact');
      pageName = 'contact';
    } else if (path === '/terms') {
      setCurrentPage('terms');
      pageName = 'terms';
    } else if (path === '/disclaimer') {
      setCurrentPage('disclaimer');
      pageName = 'disclaimer';
    } else if (path === '/privacy') {
      setCurrentPage('privacy');
      pageName = 'privacy';
    } else if (path.startsWith('/event/')) {
      setCurrentPage('event-detail');
      pageName = 'event-detail';
    } else {
      setCurrentPage('home');
      pageName = 'home';
    }
    
    // Track page view
    analytics.trackPage(pageName);
  };

  // Handle routing based on URL
  useEffect(() => {
    const path = window.location.pathname;
    handleRouteChange(path);
  }, []);

  // Listen for browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      handleRouteChange(path);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // HomePage component defined inside App to ensure it's within AuthProvider
  const HomePage = () => {
    return (
      <main className="relative">
        <HeroSlider />
        <SearchDiscovery isVisible={currentPage === 'home'} />
        <CityCelebrations />
        <FeaturedSections />
        
        {/* Advertisement Section */}
        <section className="py-8 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center">
              <AdSense 
                adSlot="1234567896" 
                adFormat="rectangle"
                className="w-full max-w-md"
                adStyle={{ display: 'block', width: '300px', height: '250px' }}
              />
            </div>
          </div>
        </section>
        
        <WelcomeSection />
      </main>
    );
  };

  const renderPage = () => {
    switch (currentPage) {
      // Admin routes
      case 'admin':
        return (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <AdminDashboard />
          </ProtectedRoute>
        );
      case 'users':
        return (
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <AdminUserManagement />
          </ProtectedRoute>
        );
        
      // Committee Admin routes
      case 'community-admin':
        return (
          <ProtectedRoute allowedRoles={['COMMITTEE_ADMIN']}>
            <CommunityAdminDashboard />
          </ProtectedRoute>
        );
        
      // Vendor Admin routes
      case 'vendor-admin':
        return (
          <ProtectedRoute allowedRoles={['VENDOR_ADMIN']}>
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">Coming Soon</h1>
                <p className="text-gray-600">Vendor Admin Dashboard is under development</p>
              </div>
            </div>
          </ProtectedRoute>
        );
        
      // Committee routes
      case 'community':
        return (
          <ProtectedRoute allowedRoles={['COMMITTEE', 'VENDOR']}>
            {user?.role === 'VENDOR' ? <VendorDashboard /> : <CommunityDashboard />}
          </ProtectedRoute>
        );
      case 'community-users':
        return (
          <ProtectedRoute allowedRoles={['COMMITTEE_ADMIN', 'VENDOR_ADMIN']}>
            <CommunityUserManagement />
          </ProtectedRoute>
        );
        
      // Public routes
      case 'all-events':
        return <ExploreEvents />;
      case 'signup':
        return <SignupPage />;
      case 'login':
        return <LoginPage />;
      case 'forgot-password':
        return <ForgotPassword />;
      case 'gallery':
        return <Gallery />;
      case 'calendar':
        return <Calendar />;
      case 'contact':
        return <Contact />;
      case 'about':
        return <AboutPage />;
      case 'faq':
        return <FAQ />;
      case 'terms':
        return <TermsAndConditions />;
      case 'disclaimer':
        return <Disclaimer />;
      case 'privacy':
        return <PrivacyPolicy />;
      case 'event-detail':
        return <EventDetailRoute />;
      case 'home':
      default:
        return <HomePage />;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {currentPage !== 'signup' && currentPage !== 'login' && currentPage !== 'forgot-password' && currentPage !== 'event-detail' && <Header />}
      {renderPage()}
      {(currentPage === 'home' || currentPage === 'about' || currentPage === 'faq' || currentPage === 'contact' || currentPage === 'terms' || currentPage === 'disclaimer' || currentPage === 'privacy') && <Footer />}
    </div>
  );
};

export default App;